// helpers/pdfOrdenPago.js
import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { getOrdenPago } from "./rutaOrdenesPago.js";

const $n = (v) =>
  Number(v || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const $d = (v) => (v ? moment(v).format("DD/MM/YYYY") : "");

export const generarPDFOrdenPago = async (ordenOrId) => {
  // Acepta la OP completa o solo el _id
  let orden = ordenOrId;

  // Si no parece populada o vino sólo el id, la buscamos
  const needsFetch =
    !orden ||
    !orden.numero ||
    !Array.isArray(orden.aplicaciones) ||
    !orden.tercero ||
    typeof orden.tercero === "string";

  if (needsFetch) {
    const id = typeof ordenOrId === "string" ? ordenOrId : ordenOrId?._id;
    if (!id) return;
    const res = await getOrdenPago(id);
    if (!res?.ok) return;
    orden = res.orden;
  }

  const doc = new jsPDF("p", "pt", "a4");

  // Header
  doc.setFillColor(210, 20, 26);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 40, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("ORDEN DE PAGO", 24, 26);

  // Datos cabecera
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);

  const terceroName =
    orden?.tercero?.transporte ||
    orden?.tercero?.razonsocial ||
    orden?.tercero?.nombre ||
    String(orden?.tercero || "");

  const clienteName =
    orden?.cliente?.razonsocial ||
    orden?.cliente?.nombre ||
    (orden?.cliente ? String(orden.cliente) : "");

  const y0 = 60;
  doc.text(`Número: ${orden.numero}`, 24, y0);
  doc.text(`Fecha: ${$d(orden.fecha)}`, 240, y0);
  doc.text(`Tercero: ${terceroName}`, 24, y0 + 18);
  if (orden?.cliente) doc.text(`Cliente: ${clienteName}`, 24, y0 + 36);

  // --- Comanda(s) y Viaje(s) en encabezado (desde aplicaciones) ---
  const appsList = Array.isArray(orden.aplicaciones) ? orden.aplicaciones : [];
  const uniqueComandas = [...new Set(appsList.map(a => a?.comanda?.codcom).filter(Boolean))];
  const uniqueViajes   = [...new Set(appsList.map(a => a?.comanda?.nroviaje).filter(Boolean))];

  const pad6 = (n) => String(n).padStart(6, "0");
  const yLineaExtra = y0 + (orden?.cliente ? 54 : 36);

  if (uniqueComandas.length > 0) {
    const txtComandas =
      uniqueComandas.length === 1
        ? `Comanda: ${pad6(uniqueComandas[0])}`
        : `Comandas: ${uniqueComandas.slice(0, 6).map(pad6).join(", ")}${uniqueComandas.length > 6 ? "…" : ""}`;
    doc.text(txtComandas, 24, yLineaExtra);
  }

  if (uniqueViajes.length > 0) {
    const txtViajes =
      uniqueViajes.length === 1
        ? `Viaje: ${uniqueViajes[0]}`
        : `Viajes: ${uniqueViajes.slice(0, 6).join(", ")}${uniqueViajes.length > 6 ? "…" : ""}`;
    doc.text(txtViajes, 240, yLineaExtra);
  }

  // Tabla Aplicaciones
  const apps = (orden.aplicaciones || []).map((a) => [
    a?.comanda?.codcom ? String(a.comanda.codcom).padStart(6, "0") : String(a.comanda || ""),
    $n(a.pactado),
    $n(a.pagadoAnterior),
    $n(a.aplicado),
  ]);

  doc.autoTable({
    startY: y0 + (orden?.cliente ? 74 : 56), // subimos por la línea extra del header
    head: [["Comanda", "Pactado", "Pagado Ant.", "Aplicado"]],
    body: apps,
    styles: { fontSize: 10, halign: "right" },
    columnStyles: { 0: { halign: "left" } },
    headStyles: { fillColor: [210, 20, 26] },
    theme: "striped",
  });

  // Tabla Pagos
  const yPagos = doc.lastAutoTable.finalY + 12;
  const pagos = (orden.pagos || []).map((p) => [
    p.medio || "",
    $n(p.importe),
    p.referencia || p.nroCheque || "",
    p.banco || "",
  ]);

  doc.autoTable({
    startY: yPagos,
    head: [["Pago", "Importe", "Ref/N° Cheque", "Banco"]],
    body: pagos,
    styles: { fontSize: 10, halign: "right" },
    columnStyles: { 0: { halign: "left" }, 2: { halign: "left" }, 3: { halign: "left" } },
    headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0] },
    theme: "striped",
  });

  // Tabla Retenciones
  const yRets = doc.lastAutoTable.finalY + 12;
  const rets = (orden.retenciones || []).map((r) => [
    r.tipo || "",
    $n(r.base),
    `${Number(r.porcentaje || 0).toFixed(2)}%`,
    $n(r.importe),
    r.comprobante || "",
  ]);

  doc.autoTable({
    startY: yRets,
    head: [["Retención", "Base", "%", "Importe", "Comp."]],
    body: rets,
    styles: { fontSize: 10, halign: "right" },
    columnStyles: { 0: { halign: "left" }, 4: { halign: "left" } },
    headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0] },
    theme: "striped",
  });

  // Totales
  const yTotals = doc.lastAutoTable.finalY + 16;
  doc.setFont("helvetica", "bold");
  doc.text(`Aplicado: $ ${$n(orden?.totales?.aplicado)}`, 24, yTotals);
  doc.text(`Retenciones: $ ${$n(orden?.totales?.retenciones)}`, 220, yTotals);
  doc.text(`Neto pagado: $ ${$n(orden?.totales?.netoPagado)}`, 420, yTotals);

  doc.save(`${orden.numero}.pdf`);
};
